import { useEffect, useState } from "react";
import FileServices from "../Common/upload/FileServices";

/** Renders a gateway logo.
 *  logoUrl conventions:
 *  - http(s) URL  -> <img> directly (external CDN logo)
 *  - "path|filename" -> fetched via fileupload POST /files1 (uploaded logos)
 */
const blobCache = new Map();

export default function GatewayLogo({ logoUrl, size = 28 }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    if (!logoUrl) return;
    if (/^(https?|blob|data):/i.test(logoUrl)) {
      setSrc(logoUrl);
      return;
    }
    const sep = logoUrl.indexOf("|");
    if (sep === -1) return;
    const path = logoUrl.slice(0, sep);
    const filename = logoUrl.slice(sep + 1);
    if (!path || !filename) return;
    const key = `${path}|${filename}`;
    if (blobCache.has(key)) {
      setSrc(blobCache.get(key));
      return;
    }
    const formData = new FormData();
    formData.append("path", path);
    formData.append("filename", filename);
    FileServices.getonefile(formData)
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "image/png" });
        const url = URL.createObjectURL(blob);
        blobCache.set(key, url);
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  if (!logoUrl) return <span className="tw-text-gray-300">—</span>;
  // Direct URLs (https, pasted URL, or local blob/data preview) render immediately
  const direct = /^(https?|blob|data):/i.test(logoUrl) ? logoUrl : null;
  const imgSrc = direct || src;
  if (!imgSrc) {
    // still resolving uploaded file via fileupload service
    return <span className="tw-text-xs tw-text-gray-400">logo…</span>;
  }
  return (
    <img
      src={imgSrc}
      alt="gateway logo"
      width={size}
      height={size}
      style={{ objectFit: "contain", borderRadius: 6, background: "#fff" }}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}
